import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { qk } from '@pps/query-keys';
import { supabase } from '@/integrations/supabase/client';
import { cleanPayload, extractEdgeError } from '@pps/supabase/payload';
import { useAuth } from '@/contexts/AuthContext';
import { useScopedClubIds } from '@/hooks/useScopedClubIds';
import { chamarIaAcoes } from '@/hooks/useAcoesDaIa';
import i18n from '@/i18n';

const SOCIAL_CONVERSATIONS_KEY = ['social_conversations'] as const;
// Mensagens da conversa especifica: `qk.whatsapp.mensagens(conversationId)`
// (IR-RQ-001), invalidada em onSuccess.
/**
 * Shim de compatibilidade (IR-RQ-001): prefixo da config do WhatsApp de todos
 * os clubes, vindo da factory. Invalida `qk.whatsapp.config(clubId)` por
 * prefix-match; o QR do gateway (`qk.whatsapp.gatewayQr`) fica de fora.
 */
export const WHATSAPP_CONFIG_KEY = qk.whatsapp.configAll;
export const INSTAGRAM_CONFIG_KEY = ['instagram_config'] as const;

export interface WhatsAppConversation {
  id: string;
  club_id: string;
  phone: string;
  contact_name: string | null;
  lead_id: string | null;
  student_id: string | null;
  last_message_at: string;
  unread_count: number;
  status: string;
  created_at: string;
  ai_suggestion: string | null;
  channel: 'whatsapp' | 'instagram';
}

export interface WhatsAppMessage {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  is_ai: boolean;
  meta_message_id: string | null;
  created_at: string;
  media_type: string | null;
  media_url: string | null;
  channel: 'whatsapp' | 'instagram';
}

export type StatusCanalMeta = 'connected' | 'attention' | 'disconnected';
export type MetodoConexaoWhatsApp = 'manual' | 'embedded' | 'qr_gateway';

/** Só colunas que o navegador pode ler — tokens ficam no Vault/servidor. */
export interface WhatsAppConfig {
  id: string;
  club_id: string;
  phone_number_id: string;
  display_phone_number: string | null;
  waba_id: string | null;
  /** `qr_gateway` = aparelho vinculado por QR code (gateway próprio, não oficial). */
  connection_method: MetodoConexaoWhatsApp | null;
  status: StatusCanalMeta;
  status_reason: string | null;
  connected_at: string | null;
  last_health_check_at: string | null;
  ai_mode: 'auto' | 'assisted' | 'off';
  /**
   * Atendente de IA (Fase 2): ações que a IA do clube NÃO pode fazer (nomes de
   * `social_ai_actions.ferramenta`; vazio = todas) e quantos minutos a
   * pré-reserva de quadra segura o horário (15–1440). Opcionais aqui porque
   * linhas lidas antes da migration 20261010200100 não os trazem.
   */
  ai_ferramentas_desligadas?: string[];
  ai_prazo_pre_reserva_min?: number;
  /**
   * Quando a pré-reserva de quadra da IA vira cobrança no Asaas (20261010230000):
   * `desligada` (padrão), `ao_confirmar` ("Confirmar e cobrar" da Agenda) ou
   * `automatica` (a IA gera o link com a pré-reserva).
   */
  ai_cobranca_pre_reserva?: 'desligada' | 'ao_confirmar' | 'automatica';
  /**
   * Regras e avisos do clube que a IA usa nas respostas (chuva, reposição…),
   * texto livre até 2000 caracteres (20261022120000). Vazio = nenhum.
   */
  ai_regras_do_clube?: string;
  /**
   * Frente 2: 'single' (padrao) ou 'network_menu' — numero da MATRIZ atende
   * a rede; conversa nova recebe menu interativo de unidades no webhook.
   */
  routing_mode: 'single' | 'network_menu';
  /** Conexão por QR code liberada para o clube (só a plataforma liga). */
  qr_gateway_allowed: boolean;
  /** Aceite dos riscos do canal não oficial; null = nunca aceitou. */
  gateway_terms_accepted_at: string | null;
  gateway_send_enabled: boolean;
}

export interface InstagramConfig {
  id: string;
  club_id: string;
  instagram_page_id: string;
  username: string | null;
  connection_method: 'manual' | 'embedded';
  status: StatusCanalMeta;
  status_reason: string | null;
  token_expires_at: string | null;
  connected_at: string | null;
  last_health_check_at: string | null;
  ai_mode: 'auto' | 'assisted' | 'off';
}

const WHATSAPP_CONFIG_COLUMNS =
  'id, club_id, phone_number_id, display_phone_number, waba_id, connection_method, status, status_reason, connected_at, last_health_check_at, ai_mode, routing_mode, qr_gateway_allowed, gateway_terms_accepted_at, gateway_send_enabled, ai_ferramentas_desligadas, ai_prazo_pre_reserva_min, ai_cobranca_pre_reserva, ai_regras_do_clube';
const INSTAGRAM_CONFIG_COLUMNS =
  'id, club_id, instagram_page_id, username, connection_method, status, status_reason, token_expires_at, connected_at, last_health_check_at, ai_mode';

export function useWhatsAppConversations(
  channelFilter?: 'whatsapp' | 'instagram',
  /**
   * Clubes marcados no filtro MÚLTIPLO (`useClubsFilter().effectiveClubIds`).
   * `undefined`/vazio = todos os clubes do escopo.
   */
  clubIdsFilter?: readonly string[]
) {
  const { scopedClubIds } = useScopedClubIds();
  const qc = useQueryClient();
  const queryClubIdsKey = (clubIdsFilter && clubIdsFilter.length > 0 ? clubIdsFilter : scopedClubIds).join('|');
  const queryClubIds = useMemo(
    () => (queryClubIdsKey ? queryClubIdsKey.split('|') : []),
    [queryClubIdsKey]
  );
  const queryClubIdSet = useMemo(
    () => new Set(queryClubIdsKey ? queryClubIdsKey.split('|') : []),
    [queryClubIdsKey]
  );

  const query = useQuery({
    queryKey: ['social_conversations', queryClubIdsKey, channelFilter],
    enabled: queryClubIds.length > 0,
    refetchInterval: 30000,
    queryFn: async () => {
      let q = supabase
        .from('social_conversations')
        .select('*');
      if (queryClubIds.length === 1) {
        q = q.eq('club_id', queryClubIds[0]!);
      } else {
        q = q.in('club_id', queryClubIds);
      }
      if (channelFilter) {
        q = q.eq('channel', channelFilter);
      }
      const { data, error } = await q.order('last_message_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as WhatsAppConversation[];
    },
  });

  useEffect(() => {
    if (queryClubIds.length === 0) return;
    // Channel name com UUID por mount: Supabase-js v2 cacheia channels por
    // nome; se removeChannel ainda não completou (async) e effect re-roda,
    // .channel(mesmoNome) retorna canal JÁ subscribed, e .on() lança
    // 'cannot add postgres_changes callbacks after subscribe()'.
    // UUID único garante canal limpo a cada mount.
    const conversationsFilter =
      queryClubIds.length === 1 ? { filter: `club_id=eq.${queryClubIds[0]}` } : {};
    const invalidateConversations = () => {
      qc.invalidateQueries({ queryKey: SOCIAL_CONVERSATIONS_KEY });
    };
    const channelName = `realtime-conversations-${queryClubIdsKey}-${channelFilter ?? 'all'}-${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_conversations',
          ...conversationsFilter,
        },
        (payload) => {
          const changedClubId =
            ((payload.new as { club_id?: string } | null)?.club_id) ??
            ((payload.old as { club_id?: string } | null)?.club_id);
          if (!changedClubId || queryClubIdSet.has(changedClubId)) {
            invalidateConversations();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_messages',
        },
        () => {
          invalidateConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelFilter, qc, queryClubIdSet, queryClubIds, queryClubIdsKey]);

  return query;
}

export function useWhatsAppMessages(conversationId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: qk.whatsapp.mensagens(conversationId),
    enabled: !!conversationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_messages')
        .select('*')
        .eq('conversation_id', conversationId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as WhatsAppMessage[];
    },
  });

  useEffect(() => {
    if (!conversationId) return;
    // UUID único por mount — mesma razão do useWhatsAppConversations acima.
    const channelName = `realtime-messages-${conversationId}-${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: qk.whatsapp.mensagens(conversationId) });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, qc]);

  return query;
}

/**
 * Envia pelo canal da conversa (whatsapp-send / instagram-send). `origem:
 * 'sugestao_ia'` = o atendente enviou a sugestão da IA: o servidor grava a
 * mensagem com `is_ai = true` (selo "IA" no chat); o resto do envio é igual.
 */
async function enviarMensagemSocial({
  conversationId,
  content,
  channel = 'whatsapp',
  origem,
}: {
  conversationId: string;
  content: string;
  channel?: string;
  origem?: 'sugestao_ia';
}) {
  const functionName = channel === 'instagram' ? 'instagram-send' : 'whatsapp-send';
  const { data, error } = await supabase.functions.invoke(functionName, {
    body: cleanPayload({ conversation_id: conversationId, content, origem }),
  });
  // Edge function whatsapp-send/instagram-send mapeia erros de rate-limit
  // (Meta Graph API) e config para codigos discretos. Mantemos extractEdgeError
  // dentro do mutationFn para localizar antes do default error toast — assim
  // usuario recebe mensagem PT/EN especifica, nao "Edge function error".
  if (error) {
    const code = await extractEdgeError(error);
    if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
    if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
    if (code === 'external_service') throw new Error(i18n.t('common.serviceError'));
    if (code === 'missing_config') throw new Error(i18n.t('common.missingConfig'));
    // Guardas do envio por QR (gateway): kill switch, só responde quem
    // escreveu antes, gateway fora do ar.
    if (code === 'send_disabled') throw new Error(i18n.t('whatsapp.envioQr.send_disabled'));
    if (code === 'no_inbound_yet') throw new Error(i18n.t('whatsapp.envioQr.no_inbound_yet'));
    if (code === 'gateway_unavailable') throw new Error(i18n.t('whatsapp.envioQr.gateway_unavailable'));
    throw error;
  }
  return data;
}

export function useSendWhatsAppMessage() {
  const qc = useQueryClient();

  return useDomainMutation({
    mutationFn: ({ conversationId, content, channel = 'whatsapp' }: { conversationId: string; content: string; channel?: string }) =>
      enviarMensagemSocial({ conversationId, content, channel }),
    // Invalida lista de conversas (last_message_at + unread_count podem mudar).
    // Mensagens da conversa especifica invalidamos manualmente em onSuccess
    // porque a queryKey eh dinamica em vars.conversationId.
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    // Sem toast de sucesso: realtime ja renderiza a mensagem na thread, toast
    // seria ruidoso (acao frequente). Erro mantem default toast com message
    // localizada vinda do mutationFn.
    toast: { error: i18n.t('toasts.messageSendError') },
    tags: { feature: 'crm', action: 'send_message' },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.whatsapp.mensagens(vars.conversationId) });
    },
  });
}

/**
 * Frente 2: transfere a conversa (e o lead vinculado) para outra unidade da
 * MESMA rede via RPC transfer_conversation (SECURITY DEFINER — valida rede e
 * permissao server-side; merge automatico se o destino ja tem conversa do
 * mesmo telefone). Retorna o id da conversa resultante.
 */
export function useTransferConversation() {
  const qc = useQueryClient();
  return useDomainMutation({
    mutationFn: async ({ conversationId, targetClubId }: { conversationId: string; targetClubId: string }) => {
      const { data, error } = await supabase.rpc('transfer_conversation', {
        p_conversation_id: conversationId,
        p_target_club_id: targetClubId,
      });
      if (error) throw error;
      return data as string;
    },
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    toast: { success: i18n.t('whatsapp.transferSuccess'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'transfer_conversation' },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.whatsapp.mensagens(vars.conversationId) });
    },
  });
}

export function useMarkConversationRead() {
  return useDomainMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('social_conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId);
      if (error) throw error;
    },
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    // Acao silenciosa (clique implicito ao abrir conversa). Sem toast.
    toast: false,
    tags: { feature: 'crm', action: 'mark_conversation_read' },
  });
}

/**
 * "Marcar como não lida" (cabeçalho da conversa). RPC
 * `mark_social_conversation_unread`: `unread_count = GREATEST(atual, 1)` — não
 * apaga contagem maior que um webhook acabou de somar e não mexe em
 * `last_message_at` (é por isso que o aviso de "nova mensagem" não dispara,
 * ver `mensagensNovas`). O estado é da conversa, vale para a equipe toda.
 */
export function useMarkConversationUnread() {
  return useDomainMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase.rpc('mark_social_conversation_unread', {
        p_conversation_id: conversationId,
      });
      if (error) throw error;
    },
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    toast: { success: i18n.t('whatsapp.markUnreadSuccess'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'mark_conversation_unread' },
  });
}

/**
 * ✓ da sugestão da IA (Fase 2 do atendente): 1) aprova as AÇÕES propostas da
 * conversa (`ia-acoes` → aprovar_pendentes: no modo assistido/QR, nada que a IA
 * propôs acontece sem este clique); 2) envia a mensagem marcada como IA; 3)
 * limpa a sugestão. Se alguma ação falhar, NÃO envia: a resposta da IA pode
 * prometer o que não aconteceu — o cartão mostra o motivo e um novo ✓ envia.
 */
export function useApproveSuggestion() {
  const qc = useQueryClient();

  return useDomainMutation({
    mutationFn: async ({
      conversationId,
      content,
      channel = 'whatsapp',
      sugestaoExibida,
    }: {
      conversationId: string;
      content: string;
      channel?: string;
      /**
       * A sugestão que a pessoa VIU ao clicar. A caixa só é limpa se ainda
       * tiver esse texto: aprovar uma pré-reserva com cobrança grava o LINK de
       * pagamento como sugestão nova (QR: só humano envia), e limpar sem
       * condição o apagaria logo depois de a resposta anterior sair.
       */
      sugestaoExibida?: string | null;
    }) => {
      await chamarIaAcoes({ conversation_id: conversationId, operacao: 'aprovar_pendentes' });
      // Writes nao-transacionais: send (edge fn) + clear ai_suggestion.
      // Se o segundo falhar, mensagem JA foi enviada — nao da pra fazer
      // rollback do envio externo. Aceitavel: usuario ve mensagem entregue
      // mas sugestao fica visivel (proximo refresh limpa via invalidate).
      // Erro do segundo step ainda eh reportado via Sentry default.
      await enviarMensagemSocial({ conversationId, content, channel, origem: 'sugestao_ia' });
      let limpar = supabase
        .from('social_conversations')
        .update({ ai_suggestion: null })
        .eq('id', conversationId);
      if (typeof sugestaoExibida === 'string') limpar = limpar.eq('ai_suggestion', sugestaoExibida);
      const { error } = await limpar;
      if (error) throw error;
    },
    invalidates: [SOCIAL_CONVERSATIONS_KEY, qk.whatsapp.acoesDaIaAll],
    // Um toast só, para qualquer etapa: a descrição traz o motivo traduzido
    // (ação que falhou ou erro do envio). Sem toast de sucesso (a mensagem
    // aparece via realtime).
    toast: { error: i18n.t('whatsapp.iaAcoes.erroAoEnviarSugestao') },
    tags: { feature: 'crm', action: 'approve_ai_suggestion' },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.whatsapp.mensagens(vars.conversationId) });
    },
    // Ações podem ter sido executadas (ou falhado) antes do erro: relê os cartões.
    onError: () => {
      qc.invalidateQueries({ queryKey: qk.whatsapp.acoesDaIaAll });
    },
  });
}

export function useDismissSuggestion() {
  return useDomainMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('social_conversations')
        .update({ ai_suggestion: null })
        .eq('id', conversationId);
      if (error) throw error;
    },
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    // Acao silenciosa (descartar sugestao). Sem toast.
    toast: false,
    tags: { feature: 'crm', action: 'dismiss_ai_suggestion' },
  });
}

export function useFindOrCreateConversation() {
  return useDomainMutation({
    mutationFn: async ({
      phone,
      contactName,
      leadId,
      clubId,
      channel = 'whatsapp',
    }: {
      phone: string;
      contactName: string;
      leadId?: string | null;
      clubId: string;
      channel?: string;
    }) => {
      const { data: existing } = await supabase
        .from('social_conversations')
        .select('id')
        .eq('phone', phone)
        .eq('club_id', clubId)
        .eq('channel', channel)
        .maybeSingle();

      if (existing) return existing.id as string;

      const { data: created, error } = await supabase
        .from('social_conversations')
        .insert({
          phone,
          contact_name: contactName,
          lead_id: leadId || null,
          club_id: clubId,
          channel,
          status: 'active',
          unread_count: 0,
          last_message_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) throw error;
      return created.id as string;
    },
    invalidates: SOCIAL_CONVERSATIONS_KEY,
    // Sem toast de sucesso: caller normalmente navega para a conversa criada,
    // toast seria redundante. Erro mantem default.
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'find_or_create_conversation' },
  });
}

export function useWhatsAppConfig(clubIdOverride?: string) {
  const { club } = useAuth();
  const { scopedClubIds } = useScopedClubIds();
  const targetClubId = clubIdOverride ?? (scopedClubIds.length === 1 ? scopedClubIds[0] : club?.id);

  const query = useQuery({
    queryKey: qk.whatsapp.config(targetClubId),
    enabled: !!targetClubId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('club_whatsapp_config')
        .select(WHATSAPP_CONFIG_COLUMNS)
        .eq('club_id', targetClubId!)
        // `.returns<>()` no lugar do parse do select: o types.ts gerado ainda
        // não traz as colunas do gateway por QR (`qr_gateway_allowed`,
        // `gateway_terms_accepted_at`, `gateway_send_enabled`), e com elas o
        // parser devolve SelectQueryError. Quando o backend regenerar os types,
        // volta ao `data as WhatsAppConfig | null` de antes.
        .returns<WhatsAppConfig[]>()
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const upsert = useDomainMutation({
    mutationFn: async (
      input: Pick<
        WhatsAppConfig,
        'ai_mode' | 'routing_mode' | 'ai_ferramentas_desligadas' | 'ai_prazo_pre_reserva_min' | 'ai_cobranca_pre_reserva'
        | 'ai_regras_do_clube'
      >,
    ) => {
      const { error } = await supabase
        .from('club_whatsapp_config')
        .upsert(
          {
            club_id: targetClubId!,
            ...input,
          },
          { onConflict: 'club_id' }
        );
      if (error) throw error;
    },
    invalidates: WHATSAPP_CONFIG_KEY,
    toast: { success: i18n.t('toasts.whatsappConfigSaved'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'upsert_whatsapp_config' },
  });

  return { config: query.data, isLoading: query.isLoading, upsert, targetClubId };
}

export function useInstagramConfig(clubIdOverride?: string) {
  const { club } = useAuth();
  const { scopedClubIds } = useScopedClubIds();
  const targetClubId = clubIdOverride ?? (scopedClubIds.length === 1 ? scopedClubIds[0] : club?.id);

  const query = useQuery({
    queryKey: ['instagram_config', targetClubId],
    enabled: !!targetClubId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('club_instagram_config')
        .select(INSTAGRAM_CONFIG_COLUMNS)
        .eq('club_id', targetClubId!)
        .maybeSingle();
      if (error) throw error;
      return data as InstagramConfig | null;
    },
  });

  const upsert = useDomainMutation({
    mutationFn: async (input: Pick<InstagramConfig, 'ai_mode'>) => {
      const { error } = await supabase
        .from('club_instagram_config')
        .upsert(
          {
            club_id: targetClubId!,
            ...input,
          },
          { onConflict: 'club_id' }
        );
      if (error) throw error;
    },
    invalidates: INSTAGRAM_CONFIG_KEY,
    toast: { success: i18n.t('toasts.instagramConfigSaved'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'upsert_instagram_config' },
  });

  return { config: query.data, isLoading: query.isLoading, upsert, targetClubId };
}
